/**
 * PartnerAccountingPage Component
 * 협력사 출금제한 페이지
 */
import React, { useState, useMemo, useCallback } from 'react';
import { usePartnerData } from '../hooks/usePartnerData';
import { useAccountManagementTab } from '../hooks/useAccountManagementTab';
import { useTableSort } from '@/shared/hooks/useTableSort';
import { useDelayedLoading } from '@/shared/hooks/useDelayedLoading';
import Pagination from '@/shared/components/dataDisplay/Pagination';
import Loading from '@/shared/components/feedback/Loading';
import ConfirmModal from '@/shared/components/feedback/ConfirmModal';
import { PartnerListItem } from '../types/partnerAccounting.types';
import RiderAccountingPage from './RiderAccountingPage';
import PageHeader from './PageHeader';
import PartnerFilterSection from './PartnerFilterSection';
import PartnerAccountTable from './PartnerAccountTable';
import '@/shared/styles/search-form.css';
import '../styles/AccountingManagement.css';

const ITEMS_PER_PAGE = 10;

interface ConfirmModalState {
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: (() => void) | null;
}

const PartnerAccountingPage: React.FC = () => {
    const {
        searchPartnerId,
        setSearchPartnerId,
        searchPartnerIdInput,
        setSearchPartnerIdInput,
        searchStatus,
        setSearchStatus,
        searchWithdrawalStatus,
        setSearchWithdrawalStatus,
        allData,
        loading,
        partnerNames,
        handleSearch,
        executeWithdrawalLimit,
        fetchPartnerList,
        resetSearchConditions,
    } = usePartnerData();

    const showLoading = useDelayedLoading(loading);

    const { activeTab, userType, handleTabChange } = useAccountManagementTab(() => {
        resetSearchConditions();
        fetchPartnerList();
    });

    const {
        sortConfig,
        sortedData: sortedAllData,
        handleSort: onSort,
    } = useTableSort<PartnerListItem>({
        data: allData || [],
        numericColumns: ['balAmt', 'unpaidAmt'],
        dateColumns: ['insDate', 'endMngDay'],
        customOrderColumns: {
            suppLvlName: ['마스터', '다이아', '골드', '실버', '브론즈', '등급없음'],
        },
    });

    const [currentPage, setCurrentPage] = useState(1);

    const [confirmModal, setConfirmModal] = useState<ConfirmModalState>({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: null,
    });

    const handleSort = useCallback((columnKey: string) => {
        onSort(columnKey);
        setCurrentPage(1);
    }, [onSort]);

    const currentPageData = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return sortedAllData.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [sortedAllData, currentPage]);

    const totalPages = useMemo(
        () => Math.ceil(sortedAllData.length / ITEMS_PER_PAGE),
        [sortedAllData.length]
    );

    const handlePageChange = useCallback((page: number) => {
        setCurrentPage(page);
    }, []);

    const handleSearchWithReset = useCallback(() => {
        setCurrentPage(1);
        handleSearch();
    }, [handleSearch]);

    const handleReset = useCallback(async () => {
        resetSearchConditions();
        setCurrentPage(1);
        await fetchPartnerList();
    }, [resetSearchConditions, fetchPartnerList]);

    const handleWithdrawalLimit = useCallback((
        suppName: string,
        suppId: string,
        withdrawalYn: string | null,
        targetAction: 'ALLOW' | 'RESTRICT'
    ) => {
        const newStatus: 'Y' | 'N' = targetAction === 'ALLOW' ? 'N' : 'Y';
        const action = targetAction === 'ALLOW' ? '출금허용' : '출금제한';
        const consequence = targetAction === 'RESTRICT'
            ? '출금제한하시면 해당 협력사와 소속 라이더들은 더 이상 출금할 수 없습니다.'
            : '출금허용하시면 해당 협력사와 소속 라이더들은 다시 출금할 수 있습니다.';

        setConfirmModal({
            isOpen: true,
            title: `${suppName} ${action}`,
            message: `${suppName}을(를) ${action} 하시겠습니까? ${consequence}`,
            onConfirm: () => {
                setConfirmModal((prev) => ({ ...prev, isOpen: false }));
                void executeWithdrawalLimit(suppId, newStatus, suppName, action);
            },
        });
    }, [executeWithdrawalLimit]);

    const closeConfirmModal = useCallback(() => {
        setConfirmModal((prev) => ({ ...prev, isOpen: false }));
    }, []);

    if (showLoading) {
        return (
            <div className="account-management">
                <PageHeader userType={userType} activeTab={activeTab} onTabChange={handleTabChange} />
                <div className="loading-container">
                    <Loading message="협력사 정보를 불러오는 중..." />
                </div>
            </div>
        );
    }

    if (activeTab === 'rider') {
        return (
            <div className="account-management">
                <PageHeader userType={userType} activeTab={activeTab} onTabChange={handleTabChange} />
                <RiderAccountingPage />
            </div>
        );
    }

    return (
        <div className="account-management">
            <PageHeader userType={userType} activeTab={activeTab} onTabChange={handleTabChange} />

            <PartnerFilterSection
                searchPartnerId={searchPartnerId}
                setSearchPartnerId={setSearchPartnerId}
                searchPartnerIdInput={searchPartnerIdInput}
                setSearchPartnerIdInput={setSearchPartnerIdInput}
                searchStatus={searchStatus}
                setSearchStatus={setSearchStatus}
                searchWithdrawalStatus={searchWithdrawalStatus}
                setSearchWithdrawalStatus={setSearchWithdrawalStatus}
                partnerNames={partnerNames}
                loading={loading}
                onSearch={handleSearchWithReset}
                onReset={handleReset}
            />

            <div className="card">
                <div className="card-body">
                    <PartnerAccountTable
                        partnerData={currentPageData}
                        handleWithdrawalLimit={handleWithdrawalLimit}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                    />

                    {totalPages > 1 && (
                        <div className="pagination-container">
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={handlePageChange}
                                maxPagesToShow={5}
                            />
                        </div>
                    )}
                </div>
            </div>

            <ConfirmModal
                isOpen={confirmModal.isOpen}
                title={confirmModal.title}
                message={confirmModal.message}
                onConfirm={confirmModal.onConfirm ?? closeConfirmModal}
                onCancel={closeConfirmModal}
            />
        </div>
    );
};

export default PartnerAccountingPage;
